import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Search } from "lucide-react";
import PageHero from "@/components/common/PageHero";
import { IMAGES } from "@/data/site";
import { api } from "@/lib/api";

export default function Players() {
  const [players, setPlayers] = useState([]);
  const [filters, setFilters] = useState({ position: "", nationality: "", age_group: "", club: "", foot: "" });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const options = useMemo(() => {
    const uniq = (k) => [...new Set(players.map((p) => p[k]).filter(Boolean))];
    return {
      position: uniq("position"),
      nationality: uniq("nationality"),
      age_group: uniq("age_group"),
      club: uniq("current_club"),
      foot: uniq("dominant_foot"),
    };
  }, [players]);

  const filtered = useMemo(() => {
    return players.filter((p) => {
      if (filters.position && p.position !== filters.position) return false;
      if (filters.nationality && p.nationality !== filters.nationality) return false;
      if (filters.age_group && p.age_group !== filters.age_group) return false;
      if (filters.club && p.current_club !== filters.club) return false;
      if (filters.foot && p.dominant_foot !== filters.foot) return false;
      return true;
    });
  }, [players, filters]);

  return (
    <>
      <PageHero
        eyebrow="Atletas representados"
        title={<>Jogadores <br /><span className="text-[#A67C00]">AF Legacy Sports</span></>}
        subtitle="Conheça os atletas acompanhados e representados pela AF Legacy Sports."
        image={IMAGES.athletePortrait}
      />

      <section className="py-16 md:py-20 border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 mb-6">
            <Search size={16} className="text-[#A67C00]" />
            <span className="section-eyebrow">Filtros</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {Object.entries(options).map(([key, values]) => (
              <select
                key={key}
                data-testid={`filter-${key}`}
                value={filters[key]}
                onChange={(e) => setFilters({ ...filters, [key]: e.target.value })}
                className="legacy-input text-sm"
              >
                <option value="">{ {position:"Posição",nationality:"Nacionalidade",age_group:"Escalão",club:"Clube",foot:"Pé dominante"}[key]}</option>
                {values.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 md:py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          {loading ? (
            <div className="text-neutral-500 text-center py-20">A carregar jogadores...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <div className="section-eyebrow mb-6">Sem resultados</div>
              <p className="text-neutral-400">Nenhum jogador corresponde aos filtros selecionados.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filtered.map((p) => (
                <Link
                  key={p.slug}
                  to={`/jogadores/${p.slug}`}
                  data-testid={`player-card-${p.slug}`}
                  className="group block bg-[#141414] border border-white/5 hover:border-[#A67C00]/40 transition-all"
                >
                  <div className="relative aspect-[3/4] overflow-hidden zoom-parent">
                    <img src={p.photo || IMAGES.athletePortrait} alt={p.name} className="absolute inset-0 w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />
                    {p.featured && (
                      <div className="absolute top-4 left-4 bg-[#A67C00] px-3 py-1 text-white text-[0.6rem] tracking-[0.3em] uppercase">
                        Destaque
                      </div>
                    )}
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="text-[#A67C00] text-[0.65rem] tracking-[0.3em] uppercase mb-2">{p.position}</div>
                      <h3 className="font-cinzel uppercase text-white text-xl leading-tight">{p.name}</h3>
                    </div>
                  </div>
                  <div className="p-5 grid grid-cols-2 gap-3 text-xs">
                    <div>
                      <div className="text-neutral-500 tracking-widest uppercase text-[0.6rem] mb-1">Nacionalidade</div>
                      <div className="text-white">{p.nationality}</div>
                    </div>
                    <div>
                      <div className="text-neutral-500 tracking-widest uppercase text-[0.6rem] mb-1">Escalão</div>
                      <div className="text-white">{p.age_group || "-"}</div>
                    </div>
                    <div>
                      <div className="text-neutral-500 tracking-widest uppercase text-[0.6rem] mb-1">Clube</div>
                      <div className="text-white">{p.current_club || "-"}</div>
                    </div>
                    <div>
                      <div className="text-neutral-500 tracking-widest uppercase text-[0.6rem] mb-1">Pé</div>
                      <div className="text-white">{p.dominant_foot || "-"}</div>
                    </div>
                  </div>
                  <div className="px-5 pb-5">
                    <div className="text-[#A67C00] text-xs tracking-[0.25em] uppercase flex items-center gap-2 group-hover:gap-4 transition-all">
                      Ver perfil <ArrowRight size={12} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
